import { useEffect, useRef, useState } from 'react'
import styles from './DiscogsSyncBadge.module.css'

interface DiscogsSyncBadgeProps {
  /** Which list this badge appears on — tunes the copy. */
  list: 'collection' | 'wantlist'
}

const PROMINENT_FOR_DAYS = 7
const FIRST_SEEN_KEY = 'tenwords:discogs-sync-badge-first-seen-v1'

function readFirstSeen(): number {
  try {
    const raw = localStorage.getItem(FIRST_SEEN_KEY)
    if (!raw) return 0
    const n = parseInt(raw, 10)
    return Number.isFinite(n) ? n : 0
  } catch { return 0 }
}

function writeFirstSeen(ts: number) {
  try { localStorage.setItem(FIRST_SEEN_KEY, String(ts)) } catch { /* ignore */ }
}

/**
 * Persistent header chip on Collection / Wantlist pages — visual reminder
 * that this page mirrors a real Discogs list. Prominent for the first
 * 7 days post-first-sight, then collapses to icon-only.
 */
export function DiscogsSyncBadge({ list }: DiscogsSyncBadgeProps) {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef<HTMLDivElement>(null)

  // Determine prominent vs minimal based on first-seen timestamp.
  const [minimal, setMinimal] = useState(false)
  useEffect(() => {
    const first = readFirstSeen()
    const now = Date.now()
    if (!first) {
      writeFirstSeen(now)
      setMinimal(false)
      return
    }
    const elapsedDays = (now - first) / 86_400_000
    setMinimal(elapsedDays >= PROMINENT_FOR_DAYS)
  }, [])

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (!wrapRef.current) return
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false)
    }
    const t = setTimeout(() => document.addEventListener('mousedown', handler), 0)
    return () => {
      clearTimeout(t)
      document.removeEventListener('mousedown', handler)
    }
  }, [open])

  const target = list === 'collection' ? 'Discogs Collection' : 'Discogs Wantlist'

  return (
    <div ref={wrapRef} className={styles.wrap}>
      <button
        type="button"
        className={`${styles.chip} ${minimal ? styles.chipMinimal : ''}`}
        onClick={() => setOpen(o => !o)}
        title={`This page mirrors your ${target}`}
        aria-label={`This page mirrors your ${target} — click for details`}
      >
        <i className="bi bi-arrow-left-right" />
        <span className={styles.chipLabel}>Syncs with Discogs</span>
      </button>

      {open && (
        <div className={styles.popover} role="dialog">
          <div className={styles.popHead}>
            <span className={styles.popIcon}><i className="bi bi-arrow-left-right" /></span>
            <span className={styles.popEyebrow}>Two-way sync</span>
          </div>
          <h4 className={styles.popTitle}>This page mirrors your {target}</h4>
          <p className={styles.popBody}>
            Every change you make here writes through to Discogs immediately —
            there is no separate local copy.
          </p>
          <ul className={styles.popList}>
            <li>
              <i className="bi bi-arrow-right-short" />
              <span><strong>Adds here</strong> add to your {target} on Discogs.</span>
            </li>
            <li>
              <i className="bi bi-arrow-right-short" />
              <span><strong>Removes here</strong> remove from your {target} on Discogs.</span>
            </li>
            <li>
              <i className="bi bi-arrow-right-short" />
              <span><strong>Changes on Discogs</strong> show up here after the next sync.</span>
            </li>
          </ul>
        </div>
      )}
    </div>
  )
}
